/**
 * Cartesian data preparation shared by the line and bar charts.
 *
 * Both plot one or more measures over a shared category axis, so the rules are
 * identical: a missing value is a hole rather than a dropped row, negatives are
 * legal, and an over-long series list cannot be folded into an "Other" bucket
 * the way part-to-whole categories can. Only the wording of the missing-value
 * note differs, which is why each chart passes its own.
 *
 * Part-to-whole preparation is a different model and lives in
 * DonutPieChart/donutUtils.
 */

import { isValidColor } from "./chartUtils";

/** One plotted measure. `key` addresses the value on each normalized point. */
export interface ChartSeries {
  /** Stable identity, equal to the data key. Drives colour and legend state. */
  id: string;
  key: string;
  label: string;
  color: string;
}

/** A normalized row: the category label plus one entry per series (null means no value). */
export interface CartesianPoint {
  x: string;
  /** Original row, handed back to the caller's click handler. */
  __source?: unknown;
  [seriesKey: string]: number | null | string | unknown;
}

export interface NormalizedSeriesData {
  points: CartesianPoint[];
  series: ChartSeries[];
  /** Rows dropped as unusable (not objects). */
  skipped: number;
  warnings: string[];
}

/** Caller-supplied series config. A bare string is shorthand for the key. */
export type SeriesInput = string | { key: string; label?: string; color?: string };

export type PointSort = "none" | "asc" | "desc";

export interface NormalizeSeriesOptions {
  xKey: string;
  /** Explicit series. Omitted, the numeric keys in the data are inferred. */
  series?: SeriesInput[];
  palette: readonly string[];
  maxSeries: number;
  sort: PointSort;
  /**
   * How this chart renders a missing value, completing the sentence
   * "N missing or non-numeric values are ...". A gap in a line and a blank
   * slot in a bar group are the same data condition shown two ways, so the
   * chart naming it is the chart that draws it.
   */
  missingNote: string;
}

/**
 * True when the plotted values straddle zero.
 *
 * Asked by every chart that stacks. A stack states that its segments sum to the
 * whole, which values of both signs do not: the bands would cancel each other
 * and the total would be shorter than its own parts.
 */
export function hasMixedSigns(points: CartesianPoint[], series: ChartSeries[]): boolean {
  let positive = false;
  let negative = false;
  points.forEach((point) => {
    series.forEach((s) => {
      const value = point[s.key];
      if (typeof value !== "number") return;
      if (value > 0) positive = true;
      if (value < 0) negative = true;
    });
  });
  return positive && negative;
}

/** True when any plotted value is below zero, so the axis has to cross it. */
export function hasNegativeValues(points: CartesianPoint[], series: ChartSeries[]): boolean {
  return points.some((point) =>
    series.some((s) => {
      const value = point[s.key];
      return typeof value === "number" && value < 0;
    }),
  );
}

/** "responseTime" and "response_time" both read as "Response time". */
export function toLabel(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Turn arbitrary caller rows into points and series that are always safe to
 * plot.
 *
 * Values that are not finite numbers become null, which recharts draws as a gap
 * in a line and an absent bar in a group. That is deliberate: dropping the whole
 * row would silently shift every other series sideways along the category axis,
 * which misrepresents the data far more than a visible hole does.
 */
export function normalizeSeriesData(
  data: unknown,
  options: NormalizeSeriesOptions,
): NormalizedSeriesData {
  const { xKey, series: seriesInput, palette, maxSeries, sort, missingNote } = options;
  const warnings: string[] = [];

  if (data == null) return { points: [], series: [], skipped: 0, warnings };

  if (!Array.isArray(data)) {
    return {
      points: [],
      series: [],
      skipped: 0,
      warnings: ["Chart data must be an array."],
    };
  }

  const rows = data.filter((row) => row != null && typeof row === "object") as Record<
    string,
    unknown
  >[];
  const skipped = data.length - rows.length;
  if (skipped > 0) {
    warnings.push(
      `${skipped} ${skipped === 1 ? "row was" : "rows were"} skipped because they are not objects.`,
    );
  }

  // Resolve which measures to plot: the caller's list, or every key that holds
  // a number somewhere in the data.
  let configs: { key: string; label?: string; color?: string }[];
  if (seriesInput?.length) {
    configs = seriesInput
      .map((entry) => (typeof entry === "string" ? { key: entry } : entry))
      .filter((entry) => entry?.key);
  } else {
    const inferred = new Map<string, true>();
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key === xKey || inferred.has(key)) return;
        const value = row[key];
        // A numeric string counts: API payloads often quote their numbers.
        if (typeof value === "number" && Number.isFinite(value)) inferred.set(key, true);
        else if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value)))
          inferred.set(key, true);
      });
    });
    configs = Array.from(inferred.keys()).map((key) => ({ key }));
  }

  if (!configs.length) {
    if (rows.length) {
      warnings.push(
        seriesInput?.length
          ? "None of the requested series were found in the data."
          : "No numeric fields were found to plot.",
      );
    }
    return { points: [], series: [], skipped, warnings };
  }

  // Colour is assigned by identity here, before any capping or legend
  // filtering, so hiding one series never repaints the others.
  let series: ChartSeries[] = configs.map((config, i) => ({
    id: config.key,
    key: config.key,
    label: config.label?.trim() || toLabel(config.key),
    color: isValidColor(config.color) ? config.color : palette[i % palette.length],
  }));

  // Past the palette, extra hues stop being tellable apart. Unlike a pie, the
  // tail cannot be summed into "Other" without inventing a measure, so the
  // extras are dropped and named.
  const cap = Math.max(1, Math.floor(maxSeries));
  if (series.length > cap) {
    const dropped = series.slice(cap);
    series = series.slice(0, cap);
    warnings.push(
      `Only the first ${cap} series are shown. Not charted: ${dropped
        .map((s) => s.label)
        .join(", ")}.`,
    );
  }

  const seenX = new Set<string>();
  let duplicateX = 0;
  let nulled = 0;

  const points: CartesianPoint[] = rows.map((row, index) => {
    const rawX = row?.[xKey];
    const label =
      typeof rawX === "string" || typeof rawX === "number" ? String(rawX).trim() : "";
    const x = label || `Point ${index + 1}`;
    if (seenX.has(x)) duplicateX += 1;
    seenX.add(x);

    const point: CartesianPoint = { x, __source: row };
    series.forEach((s) => {
      const raw = row?.[s.key];
      const value = typeof raw === "number" ? raw : Number(raw);
      const usable = raw !== null && raw !== undefined && raw !== "" && Number.isFinite(value);
      if (!usable) nulled += 1;
      point[s.key] = usable ? value : null;
    });
    return point;
  });

  if (duplicateX > 0) {
    warnings.push(
      `${duplicateX} duplicate ${duplicateX === 1 ? "value" : "values"} on the ${xKey} axis. Points are plotted in the order supplied.`,
    );
  }
  if (nulled > 0) {
    warnings.push(
      `${nulled} missing or non-numeric ${nulled === 1 ? "value is" : "values are"} ${missingNote}.`,
    );
  }

  // Drop measures that turned out to be entirely empty, so the legend does not
  // advertise a series that was never drawn.
  const populated = series.filter((s) => points.some((p) => p[s.key] != null));
  if (populated.length !== series.length) {
    const empty = series.filter((s) => !populated.includes(s));
    warnings.push(
      `${empty.map((s) => s.label).join(", ")} had no numeric values and ${empty.length === 1 ? "was" : "were"} not plotted.`,
    );
    series = populated;
  }

  if (sort !== "none") {
    // Numeric x values sort numerically; anything else falls back to locale
    // order, which is correct for ISO dates and plain labels alike.
    points.sort((a, b) => {
      const na = Number(a.x);
      const nb = Number(b.x);
      const both = Number.isFinite(na) && Number.isFinite(nb);
      const cmp = both ? na - nb : String(a.x).localeCompare(String(b.x));
      return sort === "asc" ? cmp : -cmp;
    });
  }

  return { points, series, skipped, warnings };
}
